import * as React from 'react';
import { getTranslator, Translator } from '../Translator';
import { ElectionResult, Translatable, Routes } from '../_generatedTypes.g';
import { Link, useParams } from 'react-router-dom';
import { NotFound } from './NotFound';
import { DocumentTitle } from './DocumentTitle';
import { downloadIcon } from './ui/icons';
import { turnoutPercentage } from '../Election';
import { api, ReadAs } from '../api';
import { useEffect, useState } from 'react';
import { useAppSelector } from '../hooks';

type VotingResultParams = {
    electionId: string;
}

export const VotingResult = () => {
    const token = useAppSelector(state => state.clientState.RequestVerificationToken);
    const language = useAppSelector(state => state.language);
    const t = getTranslator(language);

    const [requestFailed, setRequestFailed] = useState<boolean>(false);
    const [parties, setParties] = useState<Party[]>([]);
    const [title, setTitle] = useState<string>(t['Loading...']);
    const [voteResult, setVoteResult] = useState<ElectionResult>();

    const electionId = Number(useParams<VotingResultParams>().electionId);

    useEffect(() => {
        fetchVoteResults();
    }, [electionId]);

    const fetchVoteResults = () => {
        api(token).Elections.DownloadVoteResult<void>(electionId, 'application/json')
            .ok(processResults, ReadAs.Json)
            .fail(() => setRequestFailed(true));
    }

    const processResults = (voteResult: ElectionResult) => {
        const distinctPartiesByCode: { [code: string]: Party } =
            voteResult.ElectionResults
                .map(x => ({ Name: x.PartyName, Code: x.PartyCode, VotesForParty: x.VoteCountPerCandidate }))
                .reduceRight((acc: any, x) => ({ ...acc, [x.Code]: { ...x, VotesForParty: x.VotesForParty + (acc[x.Code] && acc[x.Code].VotesForParty || 0) } }), {});
        const codes = Object.keys(distinctPartiesByCode);
        codes.sort();
        const parties = codes.map(code => distinctPartiesByCode[code]);
        const title = t.votingResultForElection(voteResult.ElectionName);
        setVoteResult(voteResult);
        setParties(parties);
        setTitle(title);
    }

    if (requestFailed) {
        return <NotFound />
    }

    return <div>
        <DocumentTitle title={title} />
        {(!voteResult) ? t['Loading...'] : <>
            <div>
                <h2>{title} {(voteResult.IsCandidateSystem) ? `(${t['Candidates System']})` : ''}</h2>
                <table className="election__details">
                    <tbody>
                        <tr>
                            <th>{t['Blank votes']}</th><td>{voteResult.BlankVoteCount}</td>
                        </tr>
                        <tr>
                            <th>{t['Candidate vote count']}</th><td>{voteResult.VoteCount - (voteResult.BlankVoteCount === null ? 0 : voteResult.BlankVoteCount)}</td>
                        </tr>
                        <tr>
                            <th>{t['Total vote count']}</th><td>{voteResult.VoteCount}</td>
                        </tr>
                        <tr>
                            <th>{t['Turnout']}</th><td>{t.formatTurnout(turnoutPercentage(voteResult), t.localeCode, '')}</td>
                        </tr>
                        <tr>
                            <th>{t['Blank votes']}</th><td>{voteResult.BlankVoteCount || 0}</td>
                        </tr>
                    </tbody>
                </table>
                <p />
                {!voteResult.IsCandidateSystem
                    ?
                    <div>
                        {parties.map(party =>
                            <div className="electionResult__party" key={party.Code}>
                                <b>{t.translate(party.Name)}: </b>{party.VotesForParty}
                            </div>
                        )}
                    </div>
                    : ''}
                <p />
                <table className="electionResult__parties">
                    <thead>
                        <tr>
                            <th>{t['Name']}</th>
                            {!voteResult.IsCandidateSystem ? <th className="number">{t['Position']}</th> : ''}
                            <th>{t['Votes']}</th>
                        </tr>
                    </thead>
                    {voteResult.IsCandidateSystem
                        ? <tbody>{parties.map(party => renderPartyResult(voteResult, party, t))}</tbody>
                        : parties.map(party => renderPartyResult(voteResult, party, t))}
                </table>
                <p className="electionResult__downloadLink">
                    <a href={Routes.Elections.DownloadVoteResult(electionId)}>
                        {downloadIcon} {t['Download (MsExcel file)']}
                    </a>
                </p>
            </div>
        </>}
        <div className="buttonrow">
            <Link className="linkbutton" to="/">{t['Back']}</Link>
        </div>
    </div>
}

type Party = { Name: Translatable, Code: string, VotesForParty: number };

const renderPartyResult = (voteResult: ElectionResult, party: Party, t: Translator) => {
    const partyResults = voteResult.ElectionResults
        .filter(r => r.PartyCode === party.Code)
        .sort((r0, r1) => r0.Position < r1.Position && -1 || r0.Position === r1.Position && 0 || 1)
        .map(result =>
            <tr key={result.CandidateId}>
                <td>{result.CandidateName}</td>
                {!voteResult.IsCandidateSystem
                    ? <td className="number">{result.Position}</td>
                    : ''}
                <td className="number">{result.VoteCountPerCandidate}</td>
            </tr>);
    const partyHeader = !voteResult.IsCandidateSystem
        ? <tr key={party.Name.EN}>
            <td colSpan={3}>
                <b>
                    {t.translate(party.Name)} ({party.Code
                    })
                </b>
            </td>
        </tr>
        : '';
    return !voteResult.IsCandidateSystem ? <tbody>{partyHeader}{partyResults}</tbody> : <>{partyResults}</>;
}



